/* Sandboxed frame that renders Naïa's render_widget HTML. The security model */
/* (opaque-origin sandbox + strict CSP) lives in @naia-app/shared so the web */
/* widget and the server-side assignment-PDF chart embeds stay consistent. */
/* Chrome is reduced to floating buttons over the content — fullscreen + close. */

import { useState } from 'react';
import { ArrowsIn, ArrowsOut, X } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { buildSandboxedSrcDoc } from '@naia-app/shared';

/* Literal Naia token values: CSS vars can't cross the sandboxed iframe boundary. */
const WIDGET_BODY_CSS =
  'html,body{margin:0;padding:12px;font-family:system-ui,-apple-system,sans-serif;color:#0B2A4A;background:#ffffff;overflow-wrap:anywhere}' +
  '*{scrollbar-width:thin;scrollbar-color:rgba(11,42,74,0.12) transparent}' +
  '*::-webkit-scrollbar{width:8px;height:8px}*::-webkit-scrollbar-track{background:transparent}' +
  '*::-webkit-scrollbar-thumb{background:rgba(11,42,74,0.12);border-radius:999px;border:2px solid transparent;background-clip:padding-box}' +
  '*::-webkit-scrollbar-thumb:hover{background:#8A94A0;background-clip:padding-box}';

export interface WidgetFrameProps {
  html: string;
  title?: string;
  height?: number;
  onClose?: () => void;
  bridgeToolCallId?: string;
}

export default function WidgetFrame({ html, title, height = 360, onClose, bridgeToolCallId }: WidgetFrameProps) {
  const [fullscreen, setFullscreen] = useState(false);
  const srcDoc = buildSandboxedSrcDoc({ content: html, bodyCss: WIDGET_BODY_CSS, bridgeToolCallId });
  return (
    <div className={cn(
      "relative bg-card border border-border rounded-card overflow-hidden shadow-card",
      fullscreen && "fixed inset-0 z-[1000] rounded-none flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]",
    )}>
      <div className="absolute top-2 right-2 z-[2] flex gap-[6px]">
        <button
          type="button"
          onClick={() => setFullscreen((v) => !v)}
          aria-label={fullscreen ? 'Quitter le plein écran' : 'Plein écran'}
          title={fullscreen ? 'Quitter le plein écran' : 'Plein écran'}
          className="w-7 h-7 flex items-center justify-center border-none rounded-full bg-[color-mix(in_srgb,var(--naia-card)_78%,transparent)] backdrop-blur-sm text-foreground text-[14px] leading-none cursor-pointer shadow-card hover:bg-card"
        >
          {fullscreen ? <ArrowsIn size={14} /> : <ArrowsOut size={14} />}
        </button>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Fermer" className="w-7 h-7 flex items-center justify-center border-none rounded-full bg-[color-mix(in_srgb,var(--naia-card)_78%,transparent)] backdrop-blur-sm text-foreground text-[14px] leading-none cursor-pointer shadow-card hover:bg-card">
            <X size={14} />
          </button>
        )}
      </div>
      <iframe
        title={title ?? 'Naïa widget'}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        className={cn("w-full border-none block bg-card", fullscreen && "flex-1")}
        style={fullscreen ? undefined : { height }}
      />
    </div>
  );
}
